import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import SubHeader from "../../../components/Subheader/Subheader";
import CustomBreadcrumbs from '../../../components/Tools/Breadcumbs';
import { departmentBankReport } from '../../../helper/Api';
import Spinner from '../../../components/Tools/spinner';
import { toast } from 'react-toastify';
import * as xlsx from 'xlsx';

export default function AccountReport() {
    const PER_PAGE = 10;
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [openDepartments, setOpenDepartments] = useState({});
    const [openAccounts, setOpenAccounts] = useState({});
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: PER_PAGE,
        total_records: 0,
        total_pages: 1,
    });
    const { financialYears } = useSelector((state) => state.authData);
    const fy_list = financialYears.payload.fy_list;
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const [selectedFyId, setSelectedFyId] = useState(fyIdRef.current || '');

    const normalizeSchemes = (rawSchemes = []) => {
        if (!Array.isArray(rawSchemes)) return [];

        return rawSchemes
            .flatMap((group) => (Array.isArray(group) ? group : [group]))
            .filter(Boolean)
            .map((scheme) => ({
                id: scheme.id,
                name: scheme.name || '-',
                balance: Number(scheme.balance || 0),
                accumulatedDeduction: Number(scheme.accumulated_deduction ?? 0),
                totalFund: Number(scheme.total_budget || 0) + Number(scheme.total_provisional_budget || 0),
            }));
    };

    const normalizeReportData = (rawData = []) => {
        if (!Array.isArray(rawData)) return [];

        return rawData.map((item) => {
            const department = item.department_details || {};
            return {
                id: item.id,
                name: department.name || '-',
                tier: department.department_level || '-',
                parentDepartmentInfo: department.parent_department_id || '-',
                bankAccounts: (item.bank_accounts || []).map((account) => ({
                    id: account.id,
                    accountNo: account.account_no || '-',
                    accountName: account.account_name || '-',
                    balance: Number(account.account_balance || 0),
                    ifsc: account.ifsc_code || '-',
                    schemes: normalizeSchemes(account.schemes),
                })),
            };
        });
    };

    const formatAmount = (value) =>
        new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(value || 0);

    const toggleDepartment = (deptId) => {
        setOpenDepartments((prev) => ({ ...prev, [deptId]: !prev[deptId] }));
    };

    const toggleAccount = (accountId) => {
        setOpenAccounts((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
    };

    const expandAll = () => {
        const allDepartments = {};
        const allAccounts = {};

        reportData.forEach((department) => {
            allDepartments[department.id] = true;
            (department.bankAccounts || []).forEach((account) => {
                allAccounts[account.id] = true;
            });
        });

        setOpenDepartments(allDepartments);
        setOpenAccounts(allAccounts);
    };

    const collapseAll = () => {
        setOpenDepartments({});
        setOpenAccounts({});
    };

    const isAllExpanded =
        reportData.length > 0 &&
        reportData.every((department) => openDepartments[department.id]) &&
        reportData.every((department) =>
            (department.bankAccounts || []).every((account) => openAccounts[account.id])
        );

    const handlePageTrigger = () => {
            const updatedFyId = localStorage.getItem('globalDateId') || '';
            fyIdRef.current = updatedFyId;
            setSelectedFyId(updatedFyId);
    }

    const exportVisibleDataToExcel = () => {
        if (!reportData.length) {
            toast.error('No visible data available for export');
            return;
        }

        const rows = reportData.flatMap((department) =>
            (department.bankAccounts || []).flatMap((account) => {
                const schemes = account.schemes || [];

                if (!schemes.length) {
                    return [{
                        department_name: department.name,
                        department_tier: department.tier,
                        parent_department: department.parentDepartmentInfo,
                        account_name: account.accountName,
                        account_no: account.accountNo,
                        ifsc_code: account.ifsc,
                        account_balance: account.balance,
                        scheme_name: '',
                        scheme_balance: '',
                        accumulated_deduction: '',
                        total_fund: '',
                    }];
                }

                return schemes.map((scheme) => ({
                    department_name: department.name,
                    department_tier: department.tier,
                    parent_department: department.parentDepartmentInfo,
                    account_name: account.accountName,
                    account_no: account.accountNo,
                    ifsc_code: account.ifsc,
                    account_balance: account.balance,
                    scheme_name: scheme.name,
                    scheme_balance: scheme.balance,
                    accumulated_deduction: scheme.accumulatedDeduction,
                    total_fund: scheme.totalFund,
                }));
            })
        );

        const workbook = xlsx.utils.book_new();
        const worksheet = xlsx.utils.json_to_sheet(rows);
        xlsx.utils.book_append_sheet(workbook, worksheet, 'Bank Account Report');
        xlsx.writeFile(workbook, `bank-account-report-page-${pagination.page}.xlsx`);
    };

    const fetchReportData = async ({ page = 1, append = false } = {}) => {
        setLoading(true);
        try {
            const response = await departmentBankReport({
                page,
                per_page: PER_PAGE,
                fy_id: selectedFyId || '',
            });
            const rawData = response?.data?.result || [];
            const responsePagination = response?.data?.pagination || {};
            const normalizedData = normalizeReportData(rawData);

            setReportData((prev) => (append ? [...prev, ...normalizedData] : normalizedData));
            setPagination({
                page: Number(responsePagination.page || page),
                per_page: Number(responsePagination.per_page || PER_PAGE),
                total_records: Number(responsePagination.total_records || 0),
                total_pages: Number(responsePagination.total_pages || 1),
            });
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "Failed to fetch report data";
            toast.error(errorMessage);
            if (!append) {
                setReportData([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = pagination.page + 1;
        if (nextPage <= pagination.total_pages && !loadingMore) {
            fetchReportData({ page: nextPage, append: true });
        }
    };

    useEffect(() => {
        setReportData([]);
        setOpenDepartments({});
        setOpenAccounts({});
        setPagination({
            page: 1,
            per_page: PER_PAGE,
            total_records: 0,
            total_pages: 1,
        });
        fetchReportData({ page: 1, append: false });
    }, [selectedFyId])

    const canLoadMore = reportData.length > 0 && pagination.page < pagination.total_pages;

    return (
        <div>
            {loading && <div > <Spinner/> </div>}
            <SubHeader fy_list={fy_list} handlePageTrigger={handlePageTrigger}/>
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin:0}}>Bank Account Report</h4>
                    <CustomBreadcrumbs linksArray={[]} title="Bank Account Report"/>
                </div>
                <div className="col-md-6 d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                    <button
                        type="button"
                        onClick={exportVisibleDataToExcel}
                        disabled={loading || reportData.length === 0}
                        style={{
                            border: "1px solid #1f7a4f",
                            borderRadius: "4px",
                            background: "#fff",
                            color: "#1f7a4f",
                            padding: "4px 10px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: loading || reportData.length === 0 ? "not-allowed" : "pointer",
                            opacity: loading || reportData.length === 0 ? 0.6 : 1,
                        }}
                    >
                        Export Visible Data
                    </button>
                    <button
                        type="button"
                        onClick={isAllExpanded ? collapseAll : expandAll}
                        disabled={loading || reportData.length === 0}
                        style={{
                            border: "1px solid #28326f",
                            borderRadius: "4px",
                            background: "#fff",
                            color: "#28326f",
                            padding: "4px 10px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: loading || reportData.length === 0 ? "not-allowed" : "pointer",
                            opacity: loading || reportData.length === 0 ? 0.6 : 1,
                        }}
                    >
                        {isAllExpanded ? "Collapse All" : "Expand All"}
                    </button>
                </div>
            </div>
            <div style={{ padding: "0 10px 10px 10px" }}>
                    {/* {loading && <div style={{ padding: "10px", color: "#28326f" }}>Loading report...</div>} */}
                    {!loading && reportData.length === 0 && (
                        <div style={{ padding: "10px", color: "#7a7a7a" }}>No data found.</div>
                    )}
                    {reportData.map((department) => (
                        <div
                            key={department.id}
                            style={{ border: "1px solid #d6dcf5", borderRadius: "5px", marginBottom: "5px", overflow: "hidden" }}
                        >
                            <button
                                type="button"
                                onClick={() => toggleDepartment(department.id)}
                                style={{
                                    width: "100%",
                                    border: "none",
                                    background: "#dff0ff",
                                    padding: "5px 10px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    fontWeight: 600,
                                    color: "#28326f",
                                }}
                            >
                                <span style={{fontSize: '13px'}}>{department.name}</span>
                                <span>{openDepartments[department.id] ? "-" : "+"}</span>
                            </button>

                            {openDepartments[department.id] && (
                                <div style={{ padding: "5px 10px", background: "#fff" }}>
                                    <div style={{ marginBottom: "10px", color: "#445" }}>
                                        <strong>Tier:</strong> {department.tier} &nbsp; | &nbsp;
                                        <strong>Parent Department:</strong> {department.parentDepartmentInfo}
                                    </div>

                                    {department.bankAccounts.map((account) => (
                                        <div
                                            key={account.id}
                                            style={{ border: "1px solid #e8ebf8", borderRadius: "5px", marginBottom: "5px" }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => toggleAccount(account.id)}
                                                style={{
                                                    width: "100%",
                                                    border: "none",
                                                    background: "#e2f6ed",
                                                    padding: "5px 5px",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    color: "#2f356f",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <span style={{fontSize: '13px'}}>{account.accountName}</span>
                                                <span>{openAccounts[account.id] ? "-" : "+"}</span>
                                            </button>

                                            {openAccounts[account.id] && (
                                                <div style={{ padding: "5px 5px" }}>
                                                    <div style={{ marginBottom: "0px", color: "#555" }}>
                                                        <strong>Account No:</strong> {account.accountNo} &nbsp; | &nbsp;
                                                        <strong>IFSC:</strong> {account.ifsc} &nbsp; | &nbsp;
                                                        <strong>Balance:</strong> {formatAmount(account.balance)}
                                                    </div>

                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm" style={{ marginBottom: 0 }}>
                                                            <thead style={{ background: "#f6f8ff" }}>
                                                                <tr>
                                                                    <th>Scheme Name</th>
                                                                    <th>Balance</th>
                                                                    <th>Accumulated Deduction</th>
                                                                    <th>Total Fund</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {account.schemes.map((scheme) => (
                                                                    <tr key={scheme.id}>
                                                                        <td>{scheme.name}</td>
                                                                        <td>{formatAmount(scheme.balance)}</td>
                                                                        <td>{formatAmount(scheme.accumulatedDeduction)}</td>
                                                                        <td>{formatAmount(scheme.totalFund)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {!loading && canLoadMore && (
                        <div style={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
                            <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                className='navigation_button'
                            >
                                {loadingMore ? "Loading..." : "Load More"}
                            </button>
                        </div>
                    )}
            </div>
        </div>
    )
}